import cn from 'classnames';
import { v4 as uuid } from 'uuid';
import { Fragment, ReactNode, useEffect, useState } from 'react';
import styles from './tabs.module.scss';

interface ITabVariants {
  size: 'sm' | 'normal';
}

export interface ITabItem extends Partial<ITabVariants> {
  tab: { onClick?: () => void; content: ReactNode; preventDefault?: boolean };
  paneContent: ReactNode;
}

export interface ITabsProps {
  tabs: ITabItem[];
  tabOpenedByDefault?: number;
  color?: 'none' | 'blue-dark' | 'dark';
  alignment?: 'left' | 'center';
  keepMobileAppearance?: boolean;
}

const Tabs = ({
  tabs,
  tabOpenedByDefault,
  color = 'none',
  alignment = 'left',
  keepMobileAppearance = false,
}: ITabsProps): JSX.Element => {
  const [activeTab, setActiveTab] = useState<number>(0);

  useEffect(() => {
    if (!tabOpenedByDefault) return;

    setActiveTab(tabOpenedByDefault);

    return () => setActiveTab(0);
  }, [tabOpenedByDefault]);

  const changeActiveTab = (idx: number): void => setActiveTab(idx);

  /* ClassNames */

  const containerClasses = cn(styles.container, {
    [styles[`container-color-${color}`]]: color,
  });

  const tabsListClasses = cn(styles.tabs, {
    [styles[`tabs-${alignment}`]]: alignment,
    [styles.withDesktopAppearance]: !keepMobileAppearance,
  });

  const tabClasses = ({ idx, size }: ITabVariants & { idx: number }): string =>
    cn(styles.tabitem, {
      [styles.active]: activeTab === idx,
      [styles[`tabitem__size-${size}`]]: size,
      [styles[`tabitem__alignment-${alignment}`]]: alignment,
      [styles.withDesktopAppearance]: !keepMobileAppearance,
    });

  const contentClasses = cn(styles.content, {
    [styles[`content-${alignment}`]]: alignment,
  });

  /* Components */

  const tabItems = tabs.map(({ tab, size = 'normal' }, idx) => (
    <div
      key={idx}
      className={tabClasses({
        idx,
        size,
      })}
    >
      <button
        disabled={tab.preventDefault}
        type="button"
        onClick={() => {
          changeActiveTab(idx);
          tab.onClick?.();
        }}
      >
        {tab.content}
        <b />
      </button>
    </div>
  ));

  const tabPanelItems = tabs.map(
    ({ paneContent }, idx) =>
      activeTab === idx && <Fragment key={uuid()}>{paneContent}</Fragment>
  );

  return (
    <section className={containerClasses}>
      <div className={tabsListClasses}>{tabItems}</div>
      <div className={contentClasses}>{tabPanelItems}</div>
    </section>
  );
};

export default Tabs;
